import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, Button } from '@mui/material';
import { fetchMonthlyPlanningCounts } from '../api';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from "react-i18next";
import { useCustomerCurrency, useCustomerLocale } from "../context/CustomerSettingsContext";

const MonthlyPlanningsTable = ({ date, onSelectWorkcell, onLoadingChange }) => {
  const [plannings, setPlannings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();
  const locale = useCustomerLocale();
  const { formatCurrency } = useCustomerCurrency();

  const formatCount = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return t("common.na");
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 })
      .formatToParts(num)
      .map((part) => (part.type === "group" ? "," : part.value))
      .join("");
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setIsLoading(true);
    onLoadingChange?.(true);
    fetchMonthlyPlanningCounts(date)
      .then((data) => {
        if (!data || !Array.isArray(data)) {
          setPlannings([]);
          return;
        }
        // Sort by total_completed in descending order, keep Total row at end
        const sorted = [...data].filter(r => r.workcell_id !== 'Total').sort((a, b) => b.total_completed - a.total_completed);
        const totalRow = data.find(r => r.workcell_id === 'Total');
        setPlannings(totalRow ? [...sorted, totalRow] : sorted);
      })
      .catch((error) => {
        console.error(error);
        setPlannings([]);
      })
      .finally(() => {
        onLoadingChange?.(false);
        setIsLoading(false);
      });
  }, [date]);

  if (isLoading) return <div>{t("common.loading")}</div>;
  if (plannings.length === 0) return <div>{t("common.noData")}</div>;

  return (
    <Table sx={{ tableLayout: 'fixed' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 'auto' }}>{t("monthly.plannings.table.workcell")}</TableCell>
          <TableCell align="right" sx={{ width: '10rem' }}>{t("monthly.plannings.table.operations")}</TableCell>
          <TableCell align="right" sx={{ width: '10rem' }}>{t("monthly.plannings.table.operationRate")}</TableCell>
          <TableCell align="right" sx={{ width: '10rem' }}>{t("monthly.plannings.table.internalTurnover")}</TableCell>
          <TableCell sx={{ width: '10rem' }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {plannings.filter(row => row.workcell_id !== 'Total').map((row, index) => (
          <TableRow key={row.workcell_id}>
            <TableCell
              sx={{
                color: theme.palette.chart.colors[index % theme.palette.chart.colors.length],
                fontWeight: 'bold'
              }}
            >
              {row.workcell_name}
            </TableCell>
            <TableCell align="right">{formatCount(row.total_completed)}</TableCell>
            <TableCell align="right">{formatCurrency(row.operation_rate)}</TableCell>
            <TableCell align="right">{formatCurrency(row.internal_turnover)}</TableCell>
            <TableCell align="right">
              <Button
                variant="contained"
                size="small"
                onClick={() => onSelectWorkcell(row.workcell_id)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {t("common.viewDetails")}
              </Button>
            </TableCell>
          </TableRow>
        ))}
        {/* Totals row */}
        {plannings.length > 0 && plannings[plannings.length - 1].workcell_id === 'Total' && (
          <TableRow sx={{ fontWeight: 'bold', backgroundColor: theme.palette.action.hover }}>
            <TableCell component="th" scope="row">{t("common.total")}</TableCell>
            <TableCell align="right">{formatCount(plannings[plannings.length - 1].total_completed)}</TableCell>
            <TableCell />
            <TableCell align="right">{formatCurrency(plannings[plannings.length - 1].internal_turnover)}</TableCell>
            <TableCell />
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default MonthlyPlanningsTable;
